import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    AssistantTable,
    ProposalField,
    ProposalPreview,
    UiAction,
    UiBlock,
} from '../../../../common/hooks/use-assistant-api.hook';
import { ActionButton, BlockHandlers, ReplyBlocks } from '../blocks/ReplyBlocks';
import { PreviewFor } from '../blocks/Previews';
import { previewWithEdits } from '../blocks/blocks';
import { ProposalForm } from './ProposalForm';
import { ReplyTable } from './ReplyTable';
import { RichText } from './RichText';

/**
 * What a reply is made of, shared by every surface that shows a conversation.
 *
 * The side panel and the Home conversation page draw the same thread, so they
 * draw it with the same parts — two copies of "how a proposal looks" is how the
 * panel and the page would come to disagree about what the vendor is approving.
 */

/** How a proposal's side-effect class reads as a chip. */
export const PROPOSAL_TONE: Record<'write' | 'spend' | 'publish', string> = {
    write: 'bg-muted text-foreground',
    spend: 'bg-warning/15 text-warning',
    publish: 'bg-destructive/10 text-destructive',
};

export interface ReplyMessage {
    role: string;
    text: string;
    images?: string[];
    links?: Array<{ key: string; route: string; label: string }>;
    tables?: AssistantTable[];
    blocks?: UiBlock[];
}

/**
 * A message's words, then its pictures, figures and doors.
 *
 * Pictures two to a row so a set reads as a set; clicking opens the full size.
 * Tables sit under the words because a comparison written as a paragraph asks
 * the vendor to pair up numbers in their head. Doors last, outlined — an offer,
 * not the point, and nothing navigates on its own.
 */
export const MessageBody: React.FC<{
    message: ReplyMessage;
    onLink: (route: string) => void;
    /** Send a quick reply. Absent where the conversation cannot be continued. */
    onAsk?: (message: string) => void;
    busy?: boolean;
}> = ({ message: m, onLink, onAsk, busy }) => {
    const { t } = useTranslation();

    return (
        <>
            <RichText text={m.text} />

            {!!m.images?.length && (
                <div className={cn('grid gap-2', m.text && 'mt-2.5', m.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
                    {m.images.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block leading-none">
                            <img
                                src={url}
                                alt=""
                                loading="lazy"
                                className="block max-h-[260px] w-full rounded-lg border object-cover"
                            />
                        </a>
                    ))}
                </div>
            )}

            {m.tables?.map((table, i) => <ReplyTable key={i} table={table} />)}

            <ReplyBlocks blocks={m.blocks} handlers={{ onAsk: onAsk ?? (() => undefined), onLink, busy: busy || !onAsk }} />

            {!!m.links?.length && (
                <div className={cn('flex flex-wrap gap-2', m.text && 'mt-2.5')}>
                    {m.links.map((link) => (
                        <Button key={link.route} variant="outline" size="sm" onClick={() => onLink(link.route)}>
                            {t(`ASSISTANT.PAGE.${link.key.toUpperCase()}`, { defaultValue: link.label })}
                            <ArrowRight data-icon="inline-end" />
                        </Button>
                    ))}
                </div>
            )}
        </>
    );
};

export interface ProposalLike {
    actionId: string;
    summary: string;
    status: 'pending' | 'applied' | 'declined';
    sideEffect?: 'write' | 'spend' | 'publish';
    creditCost?: number;
    fields?: ProposalField[];
    preview?: ProposalPreview;
    outcome?: { followUps?: UiAction[] };
}

/**
 * A change the assistant proposed and has not made.
 *
 * Deliberately not a message: it is the one thing on screen that does
 * something, and burying it in the transcript is how a vendor scrolls past a
 * change they meant to approve. Resolved ones stay, greyed, so the
 * conversation still records what was decided. What approving does — a change,
 * a spend, something that leaves the shop — is said before the summary, and it
 * comes from the tool rather than the model's wording.
 */
export const ProposalCard: React.FC<{
    action: ProposalLike;
    edits: Record<string, unknown>;
    onEdit: (path: string, value: unknown) => void;
    onApply: () => void;
    onDecline: () => void;
    /** For the next steps an applied change offers. */
    handlers?: BlockHandlers;
}> = ({ action: a, edits, onEdit, onApply, onDecline, handlers }) => {
    const { t } = useTranslation();
    const settled = a.status !== 'pending';
    // Older proposals carry no class; they were all writes.
    const sideEffect = a.sideEffect ?? 'write';

    return (
        <div
            className={cn(
                'self-stretch rounded-xl border p-3.5',
                settled ? 'bg-muted opacity-75' : 'border-foreground bg-background',
            )}
        >
            <p className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {settled
                    ? t(`ASSISTANT.PROPOSAL.${a.status.toUpperCase()}`, a.status)
                    : t('ASSISTANT.PROPOSAL.TITLE', 'Waiting for you')}
            </p>

            {!settled && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                    <span className={cn('px-2 py-0.5 text-[11.5px] font-medium', PROPOSAL_TONE[sideEffect])}>
                        {t(`ASSISTANT.PROPOSAL.CLASS.${sideEffect.toUpperCase()}`)}
                    </span>
                    {a.creditCost != null && (
                        <span className={cn('px-2 py-0.5 text-[11.5px] font-medium', PROPOSAL_TONE.spend)}>
                            {t('ASSISTANT.PROPOSAL.COST', { count: a.creditCost })}
                        </span>
                    )}
                </div>
            )}

            <p className="text-sm leading-relaxed">
                <RichText text={a.summary} />
            </p>

            {/* How it will look, redrawn as the form above it is edited. */}
            {!settled && a.preview && (
                <PreviewFor kind={a.preview.kind} settings={previewWithEdits(a.preview, edits)} title={t('AGENTS.UI.PREVIEW.PROPOSAL') as string} />
            )}

            {!settled && <ProposalForm fields={a.fields ?? []} edits={edits} onChange={onEdit} />}

            {a.status === 'applied' && !!a.outcome?.followUps?.length && handlers && (
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{t('AGENTS.UI.NEXT')}</span>
                    {a.outcome.followUps.map((f, i) => (
                        <ActionButton key={i} action={f} handlers={handlers} primary={i === 0} />
                    ))}
                </div>
            )}

            {!settled && (
                <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" className="border-foreground" onClick={onApply}>
                        {sideEffect === 'publish'
                            ? t('ASSISTANT.PROPOSAL.PUBLISH', 'Publish')
                            : t('ASSISTANT.PROPOSAL.APPLY', 'Apply')}
                    </Button>
                    <Button variant="outline" size="sm" className="text-muted-foreground" onClick={onDecline}>
                        {t('ASSISTANT.PROPOSAL.DECLINE', 'Not now')}
                    </Button>
                </div>
            )}
        </div>
    );
};
